/**
 * Voxcanon dual-theme — initial-paint resolver and persistence helpers.
 *
 * The theme is decided in this order:
 *   1. `?theme=fantasy|scifi` URL param (dev / QA convenience)
 *   2. `vox-theme` cookie (set by the user toggle, shared across the
 *      voxcanon.com root so the choice survives subdomain hops)
 *   3. `prefers-color-scheme` — light → fantasy, dark → scifi
 *   4. Fallback: fantasy
 *
 * The user's profile preference is fetched after auth resolves and, if
 * it differs from what we picked here, applied + written back to the
 * cookie so the next first-paint matches without a flash. That flow
 * lives in the `_auth` layout.
 */

export type TVoxTheme = "fantasy" | "scifi"

const COOKIE_NAME = "vox-theme"
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

function isVoxTheme(value: unknown): value is TVoxTheme {
  return value === "fantasy" || value === "scifi"
}

function readUrlOverride(): TVoxTheme | null {
  if (typeof window === "undefined") return null
  try {
    const param = new URL(window.location.href).searchParams.get("theme")
    return isVoxTheme(param) ? param : null
  } catch {
    return null
  }
}

function readCookie(): TVoxTheme | null {
  if (typeof document === "undefined") return null
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${COOKIE_NAME}=`))
  if (!match) return null
  const value = match.slice(COOKIE_NAME.length + 1)
  return isVoxTheme(value) ? value : null
}

function readSystemPreference(): TVoxTheme {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "fantasy"
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "scifi" : "fantasy"
}

export function resolveInitialTheme(): TVoxTheme {
  return readUrlOverride() ?? readCookie() ?? readSystemPreference()
}

/**
 * Decide the cookie domain so the choice is shared across the brand's
 * subdomains in production. On localhost / *.local dev hosts we leave
 * the domain unset so the cookie is host-only.
 */
function cookieDomain(): string | null {
  if (typeof window === "undefined") return null
  const host = window.location.hostname
  if (host === "voxcanon.com" || host.endsWith(".voxcanon.com")) return ".voxcanon.com"
  if (host === "lexicanon.com" || host.endsWith(".lexicanon.com")) return ".lexicanon.com"
  return null
}

export function applyTheme(theme: TVoxTheme): void {
  if (typeof document === "undefined") return
  document.documentElement.dataset.theme = theme
}

export function persistThemeCookie(theme: TVoxTheme): void {
  if (typeof document === "undefined") return
  const parts: string[] = [`${COOKIE_NAME}=${theme}`, `Max-Age=${ONE_YEAR_SECONDS}`, "Path=/", "SameSite=Lax"]
  const domain = cookieDomain()
  if (domain) parts.push(`Domain=${domain}`)
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    parts.push("Secure")
  }
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API has poor Safari support; document.cookie is the right call here.
  document.cookie = parts.join("; ")
}

export function readPersistedTheme(): TVoxTheme | null {
  return readCookie()
}
